/**
 * PageHeader — the one header every signed-in page renders.
 *
 * A breadcrumb trail on its own row, then the page's icon in a square tile
 * framing the page's single `<h1>` and optional description, with the page's
 * buttons on the right (stacked underneath on narrow screens). The tile and
 * type sizes follow SwiftCase's page header. Purely presentational: pages
 * configure it only through props, and take the icon from the page trail so
 * it is always the page's sidebar icon, drawn larger.
 *
 *   <PageHeader
 *     crumbs={[{ label: 'TLC Group', to: '/admin' }, { label: 'Users' }]}
 *     icon={Users}
 *     title="Users"
 *     description="Manage users in this tenant"
 *     actions={<Button label="Add user" preIcon={Plus} onClick={openCreate} />}
 *   />
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Breadcrumb, type BreadcrumbItemSpec } from '../Breadcrumb/Breadcrumb';

export type PageHeaderSize = 'base' | 'sm';

export interface PageHeaderProps {
  /** The trail back up, current page last. An empty trail renders no breadcrumb. */
  crumbs: BreadcrumbItemSpec[];
  title: ReactNode;
  description?: ReactNode;
  /** The page's buttons, right-aligned beside the title. */
  actions?: ReactNode;
  /** The page's icon, shown in the square tile. Pages pass `usePageIcon()`. */
  icon?: LucideIcon;
  /** `base` is the full page header; `sm` is the compact one. */
  size?: PageHeaderSize;
  className?: string;
}

const SIZES: Record<PageHeaderSize, { row: string; tile: string; glyph: string; title: string; description: string }> = {
  base: {
    row: 'gap-6',
    tile: 'size-24 rounded-lg',
    glyph: 'size-9',
    title: 'text-5xl',
    description: 'mt-2 text-lg',
  },
  sm: {
    row: 'gap-4',
    tile: 'size-12 rounded-md',
    glyph: 'size-5',
    title: 'text-2xl',
    description: 'mt-0.5 text-sm',
  },
};

export function PageHeader({
  crumbs,
  title,
  description,
  actions,
  icon: Icon,
  size = 'base',
  className,
}: PageHeaderProps) {
  const sizes = SIZES[size];
  return (
    <header data-slot="page-header" data-size={size} className={cn('space-y-4', className)}>
      <Breadcrumb items={crumbs} />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className={cn('flex min-w-0 items-center', sizes.row)}>
          {Icon && (
            <div
              data-slot="page-header-icon"
              className={cn(
                'flex shrink-0 items-center justify-center',
                'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200',
                sizes.tile,
              )}
            >
              <Icon aria-hidden="true" className={sizes.glyph} />
            </div>
          )}
          <div className="min-w-0">
            {/* Focusable from script only, so focus can return here when a dialog closes. */}
            <h1
              tabIndex={-1}
              className={cn('font-bold text-gray-900 dark:text-white break-words outline-none', sizes.title)}
            >
              {title}
            </h1>
            {description && <p className={cn('text-gray-500 dark:text-gray-400', sizes.description)}>{description}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:pt-1">{actions}</div>}
      </div>
    </header>
  );
}
